import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight, Phone, MessageSquare, BarChart3, Shield } from 'lucide-react';
import { Button } from './ui/button';

const Hero = () => {
  const navigate = useNavigate();

  return (
    <section className="relative pt-28 pb-8 px-6 overflow-hidden" data-testid="hero-section">
      {/* Ambient glow */}
      <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-purple-600/8 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Badge */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-teal-900/40 to-purple-900/40 border border-white/10" data-testid="hero-badge">
            <Check className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-gray-200">
              Unlimited minutes on all plans &bull; 24/7 AI coverage
            </span>
          </div>
        </div>

        {/* Main Headline */}
        <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white text-center mb-6 leading-tight tracking-tight" data-testid="hero-headline">
          Your AI agent that answers calls,
          <br className="hidden md:block" />
          chats & closes leads
        </h1>

        {/* Subheadline */}
        <p className="text-base sm:text-lg text-gray-400 text-center max-w-3xl mx-auto mb-10 leading-relaxed" data-testid="hero-subtitle">
          Miadesk AI Agent handles inbound calls, live chat, outbound campaigns, invoicing, and CRM &mdash; so you never miss a lead again. Set up in 60 seconds.
        </p>

        {/* CTA Buttons */}
        <div className="flex items-center justify-center gap-4 mb-16" data-testid="hero-ctas">
          <Button
            onClick={() => navigate('/register')}
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-full px-8 py-6 text-base font-semibold shadow-lg shadow-purple-500/25 transition-all"
            data-testid="hero-start-trial-btn"
          >
            Start Free Trial
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button
            onClick={() => {
              const el = document.getElementById('demos');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            variant="outline"
            className="rounded-full px-8 py-6 text-base font-semibold border-white/15 text-gray-300 hover:bg-white/5 hover:text-white transition-all"
            data-testid="hero-see-demo-btn"
          >
            Hear a Demo
          </Button>
        </div>

        {/* Dashboard Mockup */}
        <div className="max-w-5xl mx-auto" data-testid="hero-mockup">
          <div className="relative rounded-2xl overflow-hidden border border-white/[0.08] bg-[#0c1525] shadow-2xl shadow-black/40">
            {/* Window bar */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.06] bg-[#0a1120]">
              <div className="w-3 h-3 rounded-full bg-red-500/60" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
              <div className="w-3 h-3 rounded-full bg-green-500/60" />
              <span className="ml-4 text-xs text-gray-500">miadesk.com/dashboard</span>
            </div>
            {/* Dashboard preview grid */}
            <div className="p-6 grid grid-cols-3 gap-4">
              {/* Stats row */}
              <div className="col-span-3 grid grid-cols-4 gap-3">
                {[
                  { label: 'Calls Today', value: '47', color: 'text-purple-400' },
                  { label: 'Leads Captured', value: '23', color: 'text-emerald-400' },
                  { label: 'Chats Active', value: '8', color: 'text-blue-400' },
                  { label: 'Revenue', value: '$12,340', color: 'text-amber-400' },
                ].map((s) => (
                  <div key={s.label} className="bg-white/[0.03] rounded-xl p-4 border border-white/[0.06]">
                    <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                    <p className="text-xs text-gray-500 mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
              {/* Left panel - recent calls */}
              <div className="col-span-2 bg-white/[0.02] rounded-xl p-4 border border-white/[0.06]">
                <p className="text-xs font-medium text-gray-400 mb-3 uppercase tracking-wider">Recent AI Conversations</p>
                <div className="space-y-2.5">
                  {[
                    { name: 'Sarah Mitchell', type: 'Inbound Call', tag: 'Lead', tagColor: 'text-emerald-400 bg-emerald-500/10', time: '2m ago' },
                    { name: 'James Rodriguez', type: 'Chat Widget', tag: 'Appointment', tagColor: 'text-blue-400 bg-blue-500/10', time: '5m ago' },
                    { name: 'Emily Chen', type: 'Inbound Call', tag: 'Support', tagColor: 'text-purple-400 bg-purple-500/10', time: '12m ago' },
                    { name: 'Michael Brown', type: 'Outbound', tag: 'Follow Up', tagColor: 'text-amber-400 bg-amber-500/10', time: '18m ago' },
                  ].map((c) => (
                    <div key={c.name} className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600/40 to-blue-600/40 flex items-center justify-center">
                          <span className="text-[10px] font-bold text-white">{c.name.split(' ').map(n => n[0]).join('')}</span>
                        </div>
                        <div>
                          <p className="text-sm text-white font-medium">{c.name}</p>
                          <p className="text-[10px] text-gray-500">{c.type}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.tagColor}`}>{c.tag}</span>
                        <span className="text-[10px] text-gray-600">{c.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Right panel - quick actions */}
              <div className="bg-white/[0.02] rounded-xl p-4 border border-white/[0.06]">
                <p className="text-xs font-medium text-gray-400 mb-3 uppercase tracking-wider">AI Agent</p>
                <div className="space-y-2">
                  {[
                    { icon: Phone, label: 'Inbound Calls', desc: 'AI answering', color: 'text-purple-400 bg-purple-500/10' },
                    { icon: MessageSquare, label: 'Chat Widget', desc: 'Live on site', color: 'text-blue-400 bg-blue-500/10' },
                    { icon: BarChart3, label: 'Analytics', desc: '23 leads today', color: 'text-emerald-400 bg-emerald-500/10' },
                    { icon: Shield, label: 'Spam Filter', desc: '14 blocked', color: 'text-red-400 bg-red-500/10' },
                  ].map((a) => (
                    <div key={a.label} className="flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${a.color}`}>
                        <a.icon className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-xs text-white font-medium">{a.label}</p>
                        <p className="text-[10px] text-gray-500">{a.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Gradient fade at bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#0a1628] to-transparent pointer-events-none" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
